import { useRxDocument, useRxPatchMutation } from '@/hooks/rxdb-base';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import type { MutationOptions } from '@/hooks/rxdb-base';

import type { User } from '@flintmint/core-shared/rxdb';

export type UserPreferences = User['preferences'];
type DefinedUserPreferences = NonNullable<UserPreferences>;
const SUPPORTED_USER_PREFERENCE_KEYS = ['originalBlockEditWarning', 'stream'] as const;
type SupportedUserPreferenceKey = (typeof SUPPORTED_USER_PREFERENCE_KEYS)[number];

export const useUser = () => {
  const { id: userId } = getAuthUser();
  return useRxDocument<User>('users', userId);
};

export const useUserPatch = () => {
  const { data: user } = useUser();
  const { mutate: patchUser, isLoading } = useRxPatchMutation<User>();

  const mutate = (payload: Partial<User>, mutationOptions?: MutationOptions) => {
    return patchUser(user!, payload, mutationOptions);
  };

  return { mutate, isLoading };
};

export const useUserPreferences = () => {
  const { data: user } = useUser();

  return {
    preferences: user?.preferences,
    // Preferences are optional on the user document, so absence cannot stand in for "not loaded yet".
    isUserLoaded: !!user,
  };
};

export const useUpdateUserPreferences = () => {
  const { data: user } = useUser();
  const { mutate: patchUser, isLoading } = useUserPatch();

  const mutate = (nextPreferences: Partial<DefinedUserPreferences>) => {
    if (!user) return;
    for (const key of Object.keys(nextPreferences)) {
      if (!SUPPORTED_USER_PREFERENCE_KEYS.includes(key as SupportedUserPreferenceKey)) {
        throw new Error(`Unsupported user preference key: ${key}`);
      }
    }

    return patchUser({
      preferences: {
        ...user.preferences,
        ...nextPreferences,
      } as DefinedUserPreferences,
    });
  };

  return {
    mutate,
    isLoading,
  };
};
